import { useCallback, useMemo } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useCondominium } from '../contexts/CondominioContext'
import { useCommitteeMembership } from '../hooks/useCommitteeMembership'
import { useRentalOwnerAccess } from '../hooks/useRentalOwnerAccess'
import { useRentalTenantAccess } from '../hooks/useRentalTenantAccess'
import { useTenantModulos } from '../hooks/useTenantModulos'
import { getContexto } from '../utils/contexto'
import type { Permissao } from '../utils/types'
import {
  COMMITTEE_FINANCE_NAV, DOORMAN_NAV, MANAGEMENT_NAV, RESIDENT_NAV,
  type NavLeaf, type NavModule, type NavTab,
} from './navigation.config'
import { getLastVisited, useCommitteeView } from './navState'

/**
 * Perfil de navegação — decidido por permissões, nunca pelo código do papel.
 * - gestão: tem alguma permissão de gestão (administração, financeiro, manutenção…)
 * - portaria: gere visitas/encomendas, sem gestão
 * - condómino: tem entidade (ou permissões *_PROPRIO), sem gestão nem portaria
 * Papéis de back-office só de leitura caem na gestão, como antes.
 */
export type NavProfile = 'gestao' | 'condomino' | 'portaria'

const GESTAO_PERMISSIONS: Permissao[] = [
  'CONDOMINIO_MANAGE', 'CONDOMINIO_EDITAR', 'ESTATE_MANAGE', 'ESTATE_EDITAR', 'ENTIDADE_MANAGE', 'USERS_READ',
  'FINANCAS_MANAGE', 'FINANCAS_CRIAR', 'COBRANCA_MANAGE', 'COBRANCA_CRIAR', 'COBRANCA_EDITAR', 'COBRANCA_PAGAMENTO_APROVAR',
  'COMPRA_MANAGE', 'MANUTENCAO_MANAGE', 'SOLICITACAO_MANUTENCAO_EDITAR', 'ORDEM_SERVICO_EDITAR', 'TENANT_MANAGE', 'ROLES_MANAGE',
]
const PORTARIA_PERMISSIONS: Permissao[] = ['VISITA_MANAGE', 'VISITANTE_MANAGE', 'ENCOMENDA_MANAGE']

export interface ResolvedLeaf extends NavLeaf { href: string }
export interface ResolvedTab extends Omit<NavTab, 'leaves'> { leaves: ResolvedLeaf[]; href: string }
export interface ResolvedModule extends Omit<NavModule, 'tabs'> { tabs: ResolvedTab[]; href: string }
export interface ActiveNav { module: ResolvedModule; tab: ResolvedTab; leaf: ResolvedLeaf }

/** Caminho relativo a /properties/:id ("" = painel). */
export function relativePath(pathname: string, condominioId: string): string | null {
  const base = `/properties/${condominioId}`
  if (pathname === base || pathname === `${base}/`) return ''
  return pathname.startsWith(`${base}/`) ? pathname.slice(base.length + 1).replace(/\/$/, '') : null
}

function leafMatches(leaf: NavLeaf, rel: string): boolean {
  // As rotas antigas dashboard/financeiro|acesso|manutencao continuam a ser o Painel.
  if (leaf.path === '') return rel === '' || rel.startsWith('dashboard/')
  return rel === leaf.path || rel.startsWith(`${leaf.path}/`)
}

/** Página mais específica (caminho mais longo) que corresponde, procurando pela ordem dos módulos. */
export function findInTree<M extends { tabs: { leaves: NavLeaf[] }[] }>(modules: M[], rel: string) {
  let best: { module: M; tab: M['tabs'][number]; leaf: M['tabs'][number]['leaves'][number] } | null = null
  for (const module of modules) {
    for (const tab of module.tabs) {
      for (const l of tab.leaves) {
        if (leafMatches(l, rel) && (!best || l.path.length > best.leaf.path.length)) best = { module, tab, leaf: l }
      }
    }
  }
  return best
}

export function useNavigation() {
  const { id: condominioId = '' } = useParams<{ id: string }>()
  const { pathname } = useLocation()
  const { user, hasAnyPermission } = useAuth()
  const { activeCondominium } = useCondominium()
  const { temModulo } = useTenantModulos()
  const { canSeeOwnerPages } = useRentalOwnerAccess()
  const { canSeeTenantPages } = useRentalTenantAccess()

  const profile: NavProfile = hasAnyPermission(...GESTAO_PERMISSIONS)
    ? 'gestao'
    : hasAnyPermission(...PORTARIA_PERMISSIONS)
      ? 'portaria'
      : user?.temEntidade || hasAnyPermission('UNIDADE_GERIR_PROPRIA') ? 'condomino' : 'gestao'

  const committeeStatus = useCommitteeMembership(condominioId, profile === 'condomino')
  const isCommitteeMember = committeeStatus === 'member'
  const committeeViewPref = useCommitteeView()
  const committeeView = isCommitteeMember && committeeViewPref

  const temPredios = !!activeCondominium?.temPredios
  const temBlocos = !!activeCondominium?.temBlocos
  const contexto = getContexto()
  const temEntidade = !!user?.temEntidade

  const leafVisible = useCallback((l: NavLeaf) => {
    if (l.permissions?.length && !hasAnyPermission(...l.permissions)) return false
    if (l.rental === 'owner' && !canSeeOwnerPages) return false
    if (l.rental === 'tenant' && !canSeeTenantPages) return false
    if (l.requires === 'predios' && !temPredios) return false
    if (l.requires === 'blocos' && !temBlocos) return false
    if (l.committeeFinance && profile === 'condomino' && !committeeView) return false
    return true
  }, [hasAnyPermission, canSeeOwnerPages, canSeeTenantPages, temPredios, temBlocos, profile, committeeView])

  const resolve = useCallback((modules: NavModule[], exclude: ReadonlySet<string> = new Set()): ResolvedModule[] => {
    const out: ResolvedModule[] = []
    for (const m of modules) {
      if (m.modulo && !temModulo(m.modulo)) continue
      if (m.memberOnly && !temEntidade) continue
      if (m.condominiumOnly && contexto === 'PROPRIEDADE') continue
      const tabs: ResolvedTab[] = []
      for (const t of m.tabs) {
        const leaves = t.leaves
          .filter((l) => !exclude.has(l.path) && leafVisible(l))
          .map((l) => ({ ...l, href: `/properties/${condominioId}${l.path ? `/${l.path}` : ''}` }))
        const shown = leaves.filter((l) => !l.hiddenInNav)
        if (shown.length === 0) continue
        const last = getLastVisited(condominioId, `${m.id}/${t.id}`)
        const entry = shown.find((l) => l.path === last) ?? shown[0]
        tabs.push({ ...t, leaves, href: entry.href })
      }
      if (tabs.length === 0) continue
      const lastTab = getLastVisited(condominioId, m.id)
      const entryTab = tabs.find((t) => t.id === lastTab) ?? tabs[0]
      out.push({ ...m, tabs, href: entryTab.href })
    }
    return out
  }, [temModulo, temEntidade, contexto, leafVisible, condominioId])

  const { modules, moreModules } = useMemo(() => {
    if (profile === 'gestao') return { modules: resolve(MANAGEMENT_NAV), moreModules: [] as ResolvedModule[] }
    const mainTree = profile === 'condomino'
      ? [...RESIDENT_NAV.slice(0, 3), ...(committeeView ? [COMMITTEE_FINANCE_NAV] : []), ...RESIDENT_NAV.slice(3)]
      : DOORMAN_NAV
    const main = resolve(mainTree)
    // "Mais": tudo o resto a que o perfil já tinha acesso no menu completo (nada é removido).
    const used = new Set(main.flatMap((m) => m.tabs.flatMap((t) => t.leaves.map((l) => l.path))))
    const more = resolve(MANAGEMENT_NAV.filter((m) => m.id !== 'dashboard'), used)
    return { modules: main, moreModules: more }
    // `pathname`: o destino de cada módulo é o último separador visitado (localStorage), que muda ao navegar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, committeeView, resolve, pathname])

  /** Módulo/separador/página do URL actual, no menu deste perfil. */
  const findActive = useCallback((pathname: string): ActiveNav | null => {
    const rel = relativePath(pathname, condominioId)
    if (rel === null) return null
    return findInTree([...modules, ...moreModules], rel) as ActiveNav | null
  }, [modules, moreModules, condominioId])

  return { profile, committeeStatus, isCommitteeMember, committeeView, modules, moreModules, findActive, condominioId }
}
